import type { ScenarioEventSchedule, ScenarioRow } from './types.js';

/**
 * Format a duration in **milliseconds** as a human-readable string.
 * Mirrors `wf-audit`'s `slices/common/utils/formatDuration.ts` — the
 * Fusion API reports `duration` in ms (per scenario-log + per
 * `lastSuccessfulRunDuration` field), and this function takes that
 * raw value directly. Handles days / hours / minutes / seconds with
 * the lowest non-zero unit always present.
 */
export function formatDuration(milliseconds: number): string {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return '0s';
  const parts: string[] = [];
  let remaining = Math.floor(milliseconds / 1000);
  const days = Math.floor(remaining / 86400);
  remaining %= 86400;
  const hours = Math.floor(remaining / 3600);
  remaining %= 3600;
  const minutes = Math.floor(remaining / 60);
  const secs = remaining % 60;
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (secs || parts.length === 0) parts.push(`${secs}s`);
  return parts.join(' ');
}

/**
 * Format an ISO datetime as `MM/DD/YYYY hh:mm A`. Mirrors the legacy
 * `DateTime.fromISO(...).toFormat('MM/dd/yyyy hh:mm a')` rendering
 * without pulling in luxon (~70 KB).
 */
export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => n.toString().padStart(2, '0');
  const hours24 = d.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const ampm = hours24 < 12 ? 'AM' : 'PM';
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(hours12)}:${pad(
    d.getMinutes(),
  )} ${ampm}`;
}

function formatIntervalString(intervalSeconds: number): string {
  let remaining = intervalSeconds;
  const days = Math.floor(remaining / 86400);
  remaining %= 86400;
  const hours = Math.floor(remaining / 3600);
  remaining %= 3600;
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  let result = 'Each ';
  if (days > 0) result += `${days} days `;
  if (hours > 0) result += `${hours} hours `;
  if (minutes > 0) result += `${minutes} minutes `;
  if (seconds > 0 || result === 'Each ') result += `${seconds} seconds`;
  return result.trim();
}

function formatDateString(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Render `ScenarioEventSchedule` as a human-readable string. Port of
 * `wf-audit/src/slices/scenario/utils/formatSchedule.ts` without
 * behaviour changes. Empty string when no schedule is set.
 */
export function formatSchedule(schedule: ScenarioEventSchedule | null | undefined): string {
  if (!schedule) return '';
  if (schedule.type === 'indefinitely' && (!schedule.interval || schedule.interval === 0)) {
    return '';
  }
  let result = '';
  if (schedule.interval && schedule.interval > 0) {
    result += `${formatIntervalString(schedule.interval)}\n`;
  }
  if (schedule.date) {
    result += `Once ${formatDateString(schedule.date)}\n`;
  }
  if (schedule.type === 'daily' && schedule.time) {
    result += `Daily at ${schedule.time}\n`;
  }
  if (schedule.days && schedule.type === 'weekly' && schedule.time) {
    const formattedDays = schedule.days.map((day) => DAY_NAMES[day]).join(', ');
    result += `Each ${formattedDays} at ${schedule.time}\n`;
  }
  if (schedule.days && schedule.type === 'monthly' && schedule.time) {
    result += `Each ${schedule.days.join(', ')} of month at ${schedule.time}\n`;
  }
  if (schedule.days && schedule.months && schedule.type === 'yearly' && schedule.time) {
    const formattedMonths = schedule.months.map((m) => MONTH_NAMES[m - 1]).join(', ');
    result += `Each ${formattedMonths} on ${schedule.days.join(', ')} at ${schedule.time}\n`;
  }
  return result.trim();
}

/**
 * Predicate for `searchQuery` filtering. Case-insensitive substring
 * match against:
 *   - scenario name + description + folder + modifiedBy + application
 *     labels (the always-loaded row fields)
 *   - workfront object id / name / module name
 *   - custom field name + the module names it appears in
 *   - planning object id / object name / record-type name / module name
 *
 * Sub-table fields are only checked when the corresponding map is
 * passed in `context` — the expand-panel-Search affordance threads
 * the same byScenario maps the list page already pulls so a click on
 * "search by ID" surfaces every scenario that references the clicked
 * object. Mirrors legacy
 * [wf-audit/src/slices/scenario/utils/filterBySearch.ts] semantics.
 */
export interface MatchesSearchContext {
  workfrontObjectsByScenario?: ReadonlyMap<
    string,
    ReadonlyArray<{
      workfrontId: string | null | undefined;
      workfrontName: string | null | undefined;
      fusionModuleName: string | null | undefined;
    }>
  >;
  customFieldsByScenario?: ReadonlyMap<string, { customFieldsData: unknown }>;
  planningObjectsByScenario?: ReadonlyMap<
    string,
    ReadonlyArray<{
      planningObjectId: string | null | undefined;
      objectName: string | null | undefined;
      recordTypeName: string | null | undefined;
      fusionModuleName: string | null | undefined;
    }>
  >;
}

export function matchesSearch(
  row: ScenarioRow,
  query: string,
  context?: MatchesSearchContext,
): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  const haystacks: Array<string | null | undefined> = [
    row.name,
    row.description,
    row.folderName,
    row.modifiedBy,
    ...(row.applications?.map((a) => a.label) ?? []),
  ];
  if (haystacks.some((h) => typeof h === 'string' && h.toLowerCase().includes(q))) {
    return true;
  }

  if (context?.workfrontObjectsByScenario) {
    const wfRows = context.workfrontObjectsByScenario.get(row.fusionId);
    if (wfRows) {
      for (const wf of wfRows) {
        if (matchValue(wf.workfrontId, q)) return true;
        if (matchValue(wf.workfrontName, q)) return true;
        if (matchValue(wf.fusionModuleName, q)) return true;
      }
    }
  }

  if (context?.customFieldsByScenario) {
    const cf = context.customFieldsByScenario.get(row.fusionId);
    if (cf && Array.isArray(cf.customFieldsData)) {
      for (const entry of cf.customFieldsData as ReadonlyArray<Record<string, unknown>>) {
        if (matchValue(entry.customField, q)) return true;
        const mods = entry.modules;
        if (Array.isArray(mods)) {
          for (const m of mods as ReadonlyArray<Record<string, unknown>>) {
            if (matchValue(m.fusionModuleName, q)) return true;
          }
        }
      }
    }
  }

  if (context?.planningObjectsByScenario) {
    const plRows = context.planningObjectsByScenario.get(row.fusionId);
    if (plRows) {
      for (const pl of plRows) {
        if (matchValue(pl.planningObjectId, q)) return true;
        if (matchValue(pl.objectName, q)) return true;
        if (matchValue(pl.recordTypeName, q)) return true;
        if (matchValue(pl.fusionModuleName, q)) return true;
      }
    }
  }

  return false;
}

function matchValue(value: unknown, lowerQuery: string): boolean {
  if (typeof value === 'string') return value.toLowerCase().includes(lowerQuery);
  if (typeof value === 'number') return String(value).toLowerCase().includes(lowerQuery);
  return false;
}
